import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Box, Typography, Grid } from '@mui/material';
import { mdiChevronUp } from '@mdi/js';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CanadaContainer, Icon, Link, theme } from 'daaas-ui';
import wmmsBlack from '../../assets/images/wmms-blk.svg';
import landscape from '../../assets/images/landscape.png';
import { LANGUAGE } from '../../constants/global';

/*
  - This code was copied over from daaas-ui v1.18.2, and modified to make it work (mostly imports were
    changed where relative paths were used, and html sections were removed that aren't used by us)

  - When using the daaas-ui CanadaFooter, the variable "useInRouterContext" kept resolving to "false", even though it should be true,
    so the "if" statement was failing, and termsRoute wasn't being used as the href for the "Terms and conditions" link.
 */

const Root = styled('div')(() => ({
  '& .footer__dateMod': {
    padding: theme.spacing(4, 0),
    '& dl': {
      margin: 0,
    },
    '& dt, dd': {
      display: 'inline',
    },
  },
  '& .footer__landscape': {
    background: `${theme.palette.canadaTheme.footer.landscape} url(${landscape}) no-repeat right bottom`,
    color: theme.palette.common.white,
    '& nav': {
      padding: theme.spacing(4, 0, 5.5, 0),
    },
  },
  '& .footer__landscapeList': {
    padding: 0,
    '&>li': {
      listStyle: 'none outside none',
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.up('sm')]: {
      columnGap: 0,
      columnCount: 2,
    },
    [theme.breakpoints.up('md')]: {
      columnGap: 0,
      columnCount: 3,
    },
  },
  '& .footer__brand': {
    background: theme.palette.canadaTheme.footer.brand,
    padding: theme.spacing(3, 0, 4),
  },
  '& .footer__brandList': {
    listStyleType: 'none',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(6),
    padding: theme.spacing(0),
    '&>li': {
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    '&>li:before': {
      content: '"•"',
      marginRight: theme.spacing(1.5),
    },
    [theme.breakpoints.up('sm')]: {
      columnGap: 0,
      columnCount: 2,
    },
    [theme.breakpoints.up('md')]: {
      columnGap: 0,
      columnCount: 'auto',
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(0),
      '&>li': {
        display: 'inline-block',
        marginBottom: theme.spacing(0),
      },
      '&>li:first-of-type:before': {
        content: 'none',
      },
    },
  },
  '& .footer__topOfPage': {
    lineHeight: theme.spacing(5),
    '& svg': {
      marginLeft: theme.spacing(3),
      verticalAlign: 'middle',
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  '& .footer__canadaLogo': {
    height: theme.spacing(3),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(5),
      paddingRight: theme.spacing(0),
    },
  },
}));

export default function CanadaFooter({
  // eslint-disable-next-line no-unused-vars
  lang = LANGUAGE.EN,
  // eslint-disable-next-line no-unused-vars
  dateModified,
  // eslint-disable-next-line no-unused-vars
  transactional = false,
  termsUseRouterLink = false,
  termsRoute = '/',
}) {
  const { t, i18n } = useTranslation();

  return (
    <Root>
      <CanadaContainer>
        <Box className="footer__dateMod">
          <dl>
            <Typography component="dt" variant="subtitle1">
              {`${t('dateMod')}: `}
            </Typography>
            <Typography component="dd" variant="subtitle1">
              {/* eslint-disable-next-line react/no-unknown-property */}
              <time property="dateModified">
                {`${
                  i18n.language === LANGUAGE.FR
                    ? process.env.LAST_MODIFIED_FR
                    : process.env.LAST_MODIFIED_EN
                }`}
              </time>
            </Typography>
          </dl>
        </Box>
      </CanadaContainer>
      <footer id="footer" tabIndex="-1">
        <div className="footer__brand">
          <CanadaContainer>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item component="nav" xs={12} md={9}>
                <h2 className="screen-reader-text">{t('aboutSite')}</h2>
                <ul className="footer__brandList">
                  <li>
                    {termsUseRouterLink ? (
                      <Link
                        component={RouterLink}
                        to={termsRoute}
                        underline="none"
                        variant="body2"
                      >
                        {t('terms')}
                      </Link>
                    ) : (
                      <Link
                        href={`${t('common:hrefStatcanTermsConditions')}`}
                        underline="none"
                        variant="body2"
                      >
                        {t('terms')}
                      </Link>
                    )}
                  </li>
                  <li>
                    <Link
                      href={`${t('common:hrefStatcanPrivacy')}`}
                      underline="none"
                      variant="body2"
                      target="_blank"
                    >
                      {t('privacy')}
                    </Link>
                  </li>
                </ul>
              </Grid>
              <Grid item xs={6} className="footer__topOfPage">
                <Link
                  href="#main"
                  variant="body2"
                  underline="hover"
                  color="inherit"
                >
                  {t('topOfPage')}
                  <Icon path={mdiChevronUp} size={1} />
                </Link>
              </Grid>
              <Grid item>
                <img
                  className="footer__canadaLogo"
                  src={wmmsBlack}
                  alt={t('canadaLogo')}
                  loading="lazy"
                />
              </Grid>
            </Grid>
          </CanadaContainer>
        </div>
      </footer>
    </Root>
  );
}

CanadaFooter.propTypes = {
  lang: PropTypes.oneOf(Object.values(LANGUAGE)),
  dateModified: PropTypes.string.isRequired,
  transactional: PropTypes.bool,
  termsUseRouterLink: PropTypes.bool,
  termsRoute: PropTypes.string,
};
